import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LogOut,
  Plug,
  User,
  Wand2,
  Tag,
  BookOpen,
  BarChart2,
  ThumbsDown,
  Layers,
  Ban,
  Activity,
  SlidersHorizontal,
  Gauge,
  BookMarked,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import { useAuth } from '../contexts/AuthProvider';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import ReplaiMark from './ReplaiMark';

const NAV_GROUPS = [
  {
    label: 'Team',
    items: [{ to: '/tickets', label: 'New Replai', icon: BookMarked }],
  },
  {
    label: 'Pilot',
    items: [
      { to: '/classic/overview', label: 'Overview', icon: Gauge },
      { to: '/classic/pipeline', label: 'Activity', icon: Activity },
      { to: '/classic/review', label: 'Quality', icon: ThumbsDown },
    ],
  },
  {
    label: 'Knowledge & tuning',
    items: [
      { to: '/classic/knowledge', label: 'Knowledge', icon: BookOpen },
      { to: '/classic/categories', label: 'Categories', icon: Tag },
      { to: '/classic/macros', label: 'Macros', icon: Wand2 },
      { to: '/classic/endpoints', label: 'Endpoints', icon: Plug },
    ],
  },
  {
    label: 'System',
    items: [
      { to: '/classic/settings', label: 'Settings', icon: SlidersHorizontal },
      { to: '/classic/blocked', label: 'Blocked senders', icon: Ban },
      { to: '/classic/backtest', label: 'Backtest', icon: BarChart2 },
      { to: '/classic/architecture', label: 'Architecture', icon: Layers },
    ],
  },
];

const AppShell = ({ children, title, subtitle, headerRight }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [counts, setCounts] = useState({ inbox: null, review: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axiosInstance.get('api/replai/nav-counts/');
        if (!cancelled) setCounts(data);
      } catch {
        // silent - badges are best-effort
      }
    };
    load();
    const id = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const countFor = (to) => {
    if (to === '/classic/inbox') return counts.inbox;
    if (to === '/classic/review') return counts.review;
    return null;
  };

  const initials = (user?.displayName || 'U').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <aside className="w-60 border-r border-border/70 bg-[hsl(var(--background))] flex flex-col justify-between sticky top-0 h-screen">
        <div className="px-5 py-6 space-y-8">
          <button
            type="button"
            onClick={() => navigate('/tickets')}
            className="flex items-center gap-3 focus:outline-none"
          >
            <ReplaiMark size={36} />
            <div className="flex flex-col items-start leading-tight">
              <span className="font-display text-[1.35rem] font-semibold text-foreground">Replai</span>
              <span className="text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground">LampTwist</span>
            </div>
          </button>

          <nav className="space-y-5">
            {NAV_GROUPS.map(({ label: groupLabel, items }) => (
              <div key={groupLabel} className="space-y-1">
                <div className="px-3 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
                  {groupLabel}
                </div>
                {items.map(({ to, label, icon: Icon }) => {
                  const active = location.pathname.startsWith(to);
                  const badge = countFor(to);
                  return (
                    <button
                      key={to}
                      onClick={() => navigate(to)}
                      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        active
                          ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                          : 'text-muted-foreground hover:bg-[hsl(var(--secondary))] hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="flex-1 text-left">{label}</span>
                      {typeof badge === 'number' && badge > 0 && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 rounded-full px-1.5 text-[0.65rem] font-semibold tabular-nums ${
                            active
                              ? 'bg-white/80 text-[hsl(var(--accent-foreground))]'
                              : 'bg-indigo-100 text-indigo-700'
                          }`}
                        >
                          {badge > 999 ? '999+' : badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </div>

        <div className="px-4 pb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition hover:bg-[hsl(var(--secondary))] focus:outline-none"
                title={user?.displayName}
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user?.clerkImageUrl || user?.profile?.avatar} alt={user?.displayName} />
                  <AvatarFallback className="bg-[hsl(var(--primary)/0.15)] text-[hsl(var(--primary))] font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start leading-tight">
                  <span className="text-foreground">{user?.displayName}</span>
                  <span className="text-[0.7rem] text-muted-foreground">Account</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{user?.displayName}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => logout()} className="gap-2">
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <header className="px-10 pt-10 pb-6 flex items-end justify-between gap-6">
          <div>
            <h1 className="font-display text-4xl font-semibold text-foreground">{title}</h1>
            {subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          {headerRight}
        </header>
        <div className="px-10 pb-16">{children}</div>
      </main>
    </div>
  );
};

export default AppShell;
